import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../AppIcon';

/**
 * Lightweight schedule modal for picking date/time.
 * Opened from EditPreviewModal "Schedule for Later" button.
 */
const ScheduleModal = ({ isOpen, onClose, onConfirm, platforms, disabled }) => {
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Default to next rounded-up 15-min slot, at least 10 min from now
  const getDefaultTime = () => {
    const now = new Date(Date.now() + 10 * 60 * 1000); // 10 min from now
    const minutes = Math.ceil(now.getMinutes() / 15) * 15;
    now.setMinutes(minutes, 0, 0);
    if (minutes >= 60) {
      now.setHours(now.getHours() + 1);
      now.setMinutes(0, 0, 0);
    }
    return now;
  };

  const defaultDateTime = getDefaultTime();

  const [selectedDate, setSelectedDate] = useState(
    defaultDateTime.toISOString().split('T')[0]
  );
  const [selectedTime, setSelectedTime] = useState(
    `${String(defaultDateTime.getHours()).padStart(2, '0')}:${String(defaultDateTime.getMinutes()).padStart(2, '0')}`
  );

  // Platform selection — default to all available platforms
  const [selectedPlatforms, setSelectedPlatforms] = useState({
    linkedin: !!platforms?.linkedin,
    twitter: !!platforms?.twitter,
  });

  // Sync when modal opens with new platforms
  React.useEffect(() => {
    if (isOpen) {
      setSelectedPlatforms({
        linkedin: !!platforms?.linkedin,
        twitter: !!platforms?.twitter,
      });
    }
  }, [isOpen, platforms?.linkedin, platforms?.twitter]);

  const togglePlatform = (platform) => {
    setSelectedPlatforms((prev) => {
      const next = { ...prev, [platform]: !prev[platform] };
      // At least one platform must remain selected
      if (!next.linkedin && !next.twitter) return prev;
      return next;
    });
  };

  const selectedPlatformCount = Object.values(selectedPlatforms).filter(Boolean).length;

  // Generate 15-minute interval time options
  const timeOptions = useMemo(() => {
    const options = [];
    for (let h = 0; h < 24; h++) {
      for (let m = 0; m < 60; m += 15) {
        const value = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
        const label = new Date(2000, 0, 1, h, m).toLocaleTimeString([], {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        });
        options.push({ value, label });
      }
    }
    return options;
  }, []);

  // Compute the scheduled UTC datetime
  const scheduledDate = useMemo(() => {
    if (!selectedDate || !selectedTime) return null;
    const [year, month, day] = selectedDate.split('-').map(Number);
    const [hours, minutes] = selectedTime.split(':').map(Number);
    return new Date(year, month - 1, day, hours, minutes);
  }, [selectedDate, selectedTime]);

  // Validation: must be at least 5 min in the future
  const isTooSoon = scheduledDate && scheduledDate.getTime() < Date.now() + 5 * 60 * 1000;
  const isValid = scheduledDate && !isTooSoon;

  // Format for the confirm button
  const formattedDateTime = scheduledDate
    ? scheduledDate.toLocaleDateString([], { month: 'short', day: 'numeric' }) +
      ' at ' +
      scheduledDate.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })
    : '';

  const handleConfirm = () => {
    if (!isValid || disabled) return;
    onConfirm(scheduledDate.toISOString(), userTimezone, selectedPlatforms);
  };

  // Today's date string for min attribute
  const todayStr = new Date().toISOString().split('T')[0];

  const availablePlatformCount = Object.values(platforms || {}).filter(Boolean).length;

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={onClose}
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative w-full max-w-sm bg-background border border-border rounded-2xl shadow-2xl overflow-hidden"
        >
          {/* Header */}
          <div className="px-6 pt-5 pb-3">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <Icon name="Clock" className="w-5 h-5 text-[#A78BFA]" />
                <h3 className="text-lg font-semibold text-foreground">Schedule Post</h3>
              </div>
              <button
                onClick={onClose}
                className="p-1.5 rounded-lg hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-colors"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <p className="text-sm text-muted-foreground">
              Choose when and where to publish
            </p>
          </div>

          {/* Form */}
          <div className="px-6 py-4 space-y-4">
            {/* Date */}
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-1.5">Date</label>
              <input
                type="date"
                value={selectedDate}
                min={todayStr}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-full px-3 py-2 bg-muted border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-[#A78BFA]/50 focus:border-[#A78BFA]"
              />
            </div>

            {/* Time */}
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-1.5">Time</label>
              <select
                value={selectedTime}
                onChange={(e) => setSelectedTime(e.target.value)}
                className="w-full px-3 py-2 bg-muted border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-[#A78BFA]/50 focus:border-[#A78BFA] appearance-none"
              >
                {timeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Timezone */}
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Icon name="Globe" className="w-3.5 h-3.5" />
              <span>{userTimezone}</span>
            </div>

            {/* Platforms — toggleable */}
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-1.5">Platforms</label>
              <div className="flex items-center gap-2">
                {platforms?.linkedin && (
                  <button
                    type="button"
                    onClick={() => togglePlatform('linkedin')}
                    className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition-all ${
                      selectedPlatforms.linkedin
                        ? 'text-[#0A66C2] bg-[#0A66C2]/10 border-[#0A66C2]/30'
                        : 'text-muted-foreground bg-muted border-border opacity-50 hover:opacity-75'
                    }`}
                  >
                    {selectedPlatforms.linkedin && <Icon name="Check" className="w-3 h-3" />}
                    <Icon name="Linkedin" className="w-3.5 h-3.5" />
                    <span>LinkedIn</span>
                  </button>
                )}
                {platforms?.twitter && (
                  <button
                    type="button"
                    onClick={() => togglePlatform('twitter')}
                    className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition-all ${
                      selectedPlatforms.twitter
                        ? 'text-foreground bg-foreground/10 border-foreground/30'
                        : 'text-muted-foreground bg-muted border-border opacity-50 hover:opacity-75'
                    }`}
                  >
                    {selectedPlatforms.twitter && <Icon name="Check" className="w-3 h-3" />}
                    <Icon name="X" className="w-3.5 h-3.5" />
                    <span>X</span>
                  </button>
                )}
              </div>
              {availablePlatformCount > 1 && (
                <p className="text-[10px] text-muted-foreground mt-1.5">
                  Click to toggle. At least one platform must be selected.
                </p>
              )}
            </div>

            {/* Validation error */}
            {isTooSoon && (
              <p className="text-xs text-red-400">
                Scheduled time must be at least 5 minutes from now.
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="px-6 pb-5 flex flex-col gap-2">
            <button
              onClick={handleConfirm}
              disabled={!isValid || disabled}
              className="w-full py-2.5 px-4 bg-gradient-to-r from-[#7E6BC4] to-[#A78BFA] hover:opacity-90 text-white font-medium rounded-lg transition-all shadow-lg shadow-[#A78BFA]/20 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {disabled ? (
                <>
                  <Icon name="Loader2" className="w-4 h-4 animate-spin" />
                  Scheduling...
                </>
              ) : (
                <>
                  <Icon name="Clock" className="w-4 h-4" />
                  Schedule {selectedPlatformCount === 1 ? '1 platform' : `${selectedPlatformCount} platforms`} for {formattedDateTime}
                </>
              )}
            </button>
            <button
              onClick={onClose}
              disabled={disabled}
              className="w-full py-2 px-4 text-muted-foreground hover:text-foreground text-sm transition-colors"
            >
              Cancel
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default ScheduleModal;
